import clsx from 'clsx';
import { FC, FormEvent, HTMLAttributes, PropsWithChildren, useRef } from 'react';
import { FormProvider, SubmitHandler, UseFormReturn } from 'react-hook-form';
import { merge } from '../../helpers/Utility';

// Guards against a second submit event (e.g. a double-click, or Enter while a click is still
// in flight) firing onSubmit again before React has re-rendered to reflect the in-flight state.
// The ref is set synchronously, so it closes the race that a `loading`/`disabled` prop cannot.
const useGuardedSubmit = (methods: UseFormReturn<any>, onSubmit: SubmitHandler<any>) => {
  const isSubmittingRef = useRef(false);

  return async (event: FormEvent<HTMLFormElement>) => {
    if (isSubmittingRef.current) {
      event.preventDefault();
      return;
    }

    isSubmittingRef.current = true;
    try {
      await methods.handleSubmit(onSubmit)(event);
    } finally {
      isSubmittingRef.current = false;
    }
  };
};

interface OldFormProps extends Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  methods: UseFormReturn<any>;
  onSubmit: SubmitHandler<any>;
}

export const TsForm: FC<OldFormProps> = ({ children, methods, onSubmit, className, ...props }) => {
  const handleSubmit = useGuardedSubmit(methods, onSubmit);

  return (
    //@ts-ignore Type instantiation is excessively deep and possibly infinite
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit} className={merge('', className)} {...props}>
        {children}
      </form>
    </FormProvider>
  );
};
export interface FormProps extends Omit<React.HTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  methods: UseFormReturn<any>;
  onSubmit: SubmitHandler<any>;
}

export const Form: FC<PropsWithChildren<FormProps>> = ({ children, methods, onSubmit, className, ...props }) => {
  const handleSubmit = useGuardedSubmit(methods, onSubmit);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit} className={clsx('', className)} {...props}>
        {children}
      </form>
    </FormProvider>
  );
};
